import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const NAV_LINKS = [
    { label: "Concept", href: "#how-it-works" },
    { label: "Sécurité", href: "#trust-safety" },
    { label: "Guides", href: "#guides" },
    { label: "Témoignages", href: "#testimonials" },
    { label: "FAQ", href: "#faq-section" },
    { label: "Contact", href: "#contact-section" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="nav-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-slate-200"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="container-x flex items-center justify-between h-20">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    className={`flex items-center transition-all ${
                        scrolled
                            ? "px-0 py-0 bg-transparent"
                            : "px-4 py-2 bg-white shadow-md"
                    }`}
                >
                    <Logo size="default" />
                </a>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={`text-sm font-medium tracking-wide transition-colors relative group ${
                                scrolled
                                    ? "text-slate-600 hover:text-ink"
                                    : "text-white/80 hover:text-white"
                            }`}
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 w-0 h-px bg-signal transition-all duration-300 group-hover:w-full" />
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="https://carvaelis.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-cta-primary"
                        className={`hidden sm:inline-flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${
                            scrolled
                                ? "bg-ink text-white hover:bg-signal"
                                : "bg-signal text-white hover:bg-white hover:text-ink"
                        }`}
                    >
                        Espace Carvaelis
                        <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                    </a>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        className={`lg:hidden w-10 h-10 flex items-center justify-center ${
                            scrolled ? "text-ink" : "text-white"
                        }`}
                        aria-label="Menu"
                    >
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="lg:hidden bg-white border-t border-slate-200"
                >
                    <div className="container-x py-6 flex flex-col gap-4">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                className="text-base font-medium text-slate-700 hover:text-signal py-2 border-b border-slate-100"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="https://carvaelis.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-mobile-cta"
                            className="mt-2 inline-flex items-center justify-center gap-2 bg-ink text-white px-5 py-4 text-sm font-bold uppercase tracking-wider"
                        >
                            Espace Carvaelis
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
